<template>
  <!-- processing update view -->
  <v-card
    flat
    class="py-5 px-1 px-md-5 d-flex flex-column justify-center align-center"
    v-if="processing"
  >
    <v-card-title>
      Processing
      <PulseLoader />
    </v-card-title>
    <LoadingCard />
  </v-card>
  <!-- normal view -->
  <v-card flat class="py-5 px-1 px-md-5 bg-background" v-else>
    <v-card-text class="mb-10">
      <!-- loading card -->
      <v-row no-gutters align="center" v-if="fetching">
        <v-skeleton-loader type="avatar" class="mr-3" />
        <v-skeleton-loader type="text, text" width="30%" />
        <v-spacer />
      </v-row>
      <!-- authed user card -->
      <v-row no-gutters v-else-if="user">
        <v-avatar color="pwt_gainsboro" class="mr-3 mt-1">
          <img v-if="avatarSrc" :src="avatarSrc" width="100%" height="auto" />
          <v-icon v-else color="pwt_light_grey"> mdi-account-circle </v-icon>
        </v-avatar>
        <v-card-subtitle class="d-flex flex-column text-pwt_black pt-0">
          <span class="font-weight-medium text-h6">
            {{ user.fullName }}
          </span>
          <span>
            {{ user.email }}
          </span>
        </v-card-subtitle>
        <v-spacer />
      </v-row>
      <!-- guest user card -->
      <v-row no-gutters align="center" v-else>
        <v-avatar color="pwt_gainsboro" class="mr-3 mt-1">
          <img v-if="avatarSrc" :src="avatarSrc" width="100%" height="auto" />
          <v-icon v-else color="pwt_light_grey"> mdi-account-circle </v-icon>
        </v-avatar>
        <div>
          <v-card-title class="pb-0" style="font-size: 20px"> Guest user </v-card-title>
          <v-card-subtitle class="text-black" style="font-size: 14px">
            please
            <a
              @click.stop="handleLogin"
              class="text-decoration-underline text-pwt_palatinate_blue cursor-pointer"
            >
              login
            </a>
            to see your details
          </v-card-subtitle>
        </div>
      </v-row>
    </v-card-text>
    <v-card elevation="3" class="pa-5 rounded-lg mb-5 bg-background" :disabled="!user">
      <v-card-text class="pb-0">
        <v-row no-gutters :align="mdAndUp ? 'center' : 'start'" class="flex-column flex-md-row">
          <v-col cols="12" sm="2" class="d-flex align-center mb-2 mb-md-0">
            <span class="mr-3">Profile picture</span>
          </v-col>
          <v-col cols="12" sm="10" class="d-flex">
            <v-avatar color="pwt_gainsboro" class="mr-3">
              <img v-if="avatarSrc" :src="avatarSrc" width="100%" height="auto" />
              <v-icon v-else color="pwt_light_grey"> mdi-account-circle </v-icon>
            </v-avatar>
            <v-spacer v-if="!showFileInput" />
            <TextBtn v-if="!showFileInput" @click="showFileInput = true"> Change picture </TextBtn>
            <v-file-input
              v-if="showFileInput"
              accept="image/*"
              label="Upload a new profile image"
              dense
              class="pt-4"
              v-model="avatar"
              prepend-icon="mdi-camera"
              :style="mdAndUp ? '' : 'width: 100%'"
            ></v-file-input>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-text>
        <v-row no-gutters class="flex-column flex-sm-row mb-5">
          <v-col cols="12" sm="2" class="d-flex align-center">
            <span class="mr-3"> Account name </span>
          </v-col>
          <v-col cols="12" sm="10" class="d-flex">
            <v-text-field
              class="rounded-lg mr-sm-2"
              label="First name"
              v-model="firstName"
              hide-details
            ></v-text-field>
            <v-text-field
              class="rounded-lg ml-sm-2"
              label="Last name"
              v-model="lastName"
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row no-gutters class="flex-column flex-sm-row mb-5">
          <v-col cols="12" sm="2" class="d-flex align-center">
            <span> Job title </span>
          </v-col>
          <v-col cols="12" sm="10">
            <v-text-field hide-details v-model="jobTitle" />
          </v-col>
        </v-row>
        <v-row no-gutters class="flex-column flex-sm-row mb-5">
          <v-col cols="12" sm="2" class="d-flex align-center">
            <span> Organisation </span>
          </v-col>
          <v-col cols="12" sm="10">
            <v-text-field hide-details v-model="org" />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card-text>
      <v-row>
        <v-spacer></v-spacer>
        <OutlinedBtn v-if="hasChanges" class="mt-n2 mr-2" @click="cancel"> Cancel </OutlinedBtn>
        <ElevatedBtn class="mt-n2" @click="SaveAndUploadPreCheck" :disabled="!hasChanges">
          Save changes
        </ElevatedBtn>
      </v-row>
    </v-card-text>
  </v-card>
</template>
<script lang="ts" setup>
import Driver from '@/classes/user_classes/driver'
import ElevatedBtn from '@/components/ui-elements/buttons/ElevatedBtn.vue'
import OutlinedBtn from '@/components/ui-elements/buttons/OutlinedBtn.vue'
import TextBtn from '@/components/ui-elements/buttons/TextBtn.vue'
import LoadingCard from '@/components/ui-elements/loaders/LoadingCard.vue'
import PulseLoader from '@/components/ui-elements/loaders/PulseLoader.vue'
import keycloak from '@/keycloak'
import {
  ActionTypes,
  GettersTypes,
  MutationTypes,
  type PreAuthAction,
  type State,
} from '@/store/store_types'
import type { DirectusDataToUpdate } from '@/types/directus-types'
import getAssetSrc from '@/utils/getAssetSrc'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'

const store = useStore<State>()
const { mdAndUp } = useDisplay()

const firstName = ref('')
const lastName = ref('')
const avatar = ref<File | null>(null)
const showFileInput = ref(false)
const jobTitle = ref('')
const org = ref('')

const driver = computed<Driver | undefined>(() => store.getters[GettersTypes.currentDriver])
const fetching = computed<boolean>(() => store.getters[GettersTypes.fetching])
const processing = computed(() => store.state.updatingUserStatus)
const user = computed(() => store.state.user)
const avatarSrc = computed(() => driver.value?.profilePicSrc ?? getAssetSrc('No account icon.svg'))
const hasChanges = computed(() => hasChanged())

function setInitialValues() {
  firstName.value = user.value?.firstName ?? ''
  lastName.value = user.value?.lastName ?? ''
  jobTitle.value = user.value?.job_title ?? ''
  org.value = user.value?.organisation ?? ''
}

const isAuthorised = computed(() => store.state.authorizedFlag)

function SaveAndUploadPreCheck() {
  if (isAuthorised.value) {
    saveAndUpload()
  } else {
    store.commit(MutationTypes.setRetuning_auth_action, 'edit_user' as PreAuthAction)
    store.commit(MutationTypes.setShow_account_needed, true) // show account needed alert
  }
}

function saveAndUpload() {
  // guard clause
  if (!user.value) return
  if (!hasChanges.value) return
  // check if data has changed from initial values and prep object for dispatch.
  const data: DirectusDataToUpdate = {
    avatar: avatar.value ?? undefined,
    firstName: firstName.value !== user.value.firstName ? firstName.value : undefined,
    lastName: lastName.value !== user.value.lastName ? lastName.value : undefined,
    job_title: jobTitle.value !== user.value.job_title ? jobTitle.value : undefined,
    organisation: org.value !== user.value.organisation ? org.value : undefined,
  }
  store.dispatch(ActionTypes.updateUser, data)
}

function hasChanged(): boolean {
  if (!isAuthorised.value) return false
  return (
    firstName.value !== user.value?.firstName ||
    lastName.value !== user.value?.lastName ||
    avatar.value !== null ||
    jobTitle.value !== user.value?.job_title ||
    org.value !== user.value?.organisation
  )
}

function cancel() {
  setInitialValues()
  showFileInput.value = false
  avatar.value = null
}

onMounted(() => {
  nextTick(() => {
    setInitialValues()
  })
})

function handleLogin() {
  const redirectUri =
    window.location.origin +
    '/#/login/' +
    new URLSearchParams(
      'location=' +
        (window.location.hash.includes('location=')
          ? window.location.hash.replace(/&location=.*/, '')
          : window.location.hash) +
        '&' +
        window.location.search,
    ).toString()

  keycloak.login({
    redirectUri,
  })
}
</script>
